import { TEST_IDS } from "@/constants/testIds";
import { Download, Loader2, Upload, X } from "lucide-react";
import { useState } from "react";
import { GoogleDriveIcon } from "../../header/icons/GoogleDriveIcon";
import { getFileIcon } from "./FileMentionDropdown";
import { FileViewerModal } from "./FileViewerModal";

// Get background color based on file extension (macOS-style)
const getFileBackgroundColor = (filename) => {
  if (!filename) return "bg-blue-500";

  const extension = filename.split(".").pop()?.toLowerCase();

  // PDF files - red/pink
  if (extension === "pdf") {
    return "bg-red-500";
  }

  // Image files - blue
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(extension)) {
    return "bg-blue-500";
  }

  // Code files - blue
  if (
    [
      "js",
      "jsx",
      "ts",
      "tsx",
      "html",
      "css",
      "py",
      "java",
      "c",
      "cpp",
      "php",
    ].includes(extension)
  ) {
    return "bg-blue-500";
  }

  // JSON files - blue
  if (["json"].includes(extension)) {
    return "bg-blue-500";
  }

  // Text/Document files - blue
  if (["txt", "md", "doc", "docx", "rtf"].includes(extension)) {
    return "bg-blue-500";
  }

  // Spreadsheet files - green
  if (["csv", "xls", "xlsx"].includes(extension)) {
    return "bg-green-500";
  }

  // Archive files - purple
  if (["zip", "rar", "tar", "gz", "7z"].includes(extension)) {
    return "bg-purple-500";
  }

  // Audio files - orange
  if (["mp3", "wav", "ogg", "flac", "m4a"].includes(extension)) {
    return "bg-orange-500";
  }

  // Video files - purple
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(extension)) {
    return "bg-purple-500";
  }

  // Default - blue
  return "bg-blue-500";
};

export function FilePreviewList({
  files,
  onRemoveFile,
  onImageClick,
  isUploading: globalIsUploading,
}) {
  const [viewerFile, setViewerFile] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  // Log each file individually to see what triggers the upload logic
  files.forEach((file, index) => {
    const isImage = file.type?.startsWith("image/");
    const imageUrl = file.localUrl || file.publicUrl || file.url;

    if (isImage && imageUrl) {
      const isUploading =
        globalIsUploading ||
        file.status === "uploading" ||
        file.status === "loading" ||
        file.isLoading === true;
    } else {
      const isPdf = file.name?.toLowerCase().endsWith(".pdf");
      const isTextFile = file.name
        ?.toLowerCase()
        .match(
          /\.(txt|md|json|js|jsx|ts|tsx|html|css|py|java|c|cpp|php|xml|yaml|yml|csv)$/
        );

      if (isPdf || isTextFile || !isImage) {
        const isUploading =
          globalIsUploading ||
          file.status === "uploading" ||
          file.status === "loading" ||
          file.isLoading === true;
      }
    }
  });

  if (files.length === 0) return null;

  const handleFileClick = (file) => {
    const isPdf = file.name?.toLowerCase().endsWith(".pdf");
    const isTextFile = file.name
      ?.toLowerCase()
      .match(
        /\.(txt|md|json|js|jsx|ts|tsx|html|css|py|java|c|cpp|php|xml|yaml|yml|csv)$/
      );

    if (isPdf || isTextFile) {
      setViewerFile(file);
      setIsViewerOpen(true);
    }
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
    setViewerFile(null);
  };

  return (
    <>
      <div className="px-3 pt-3 pb-2 file-preview-container" data-testid={TEST_IDS.FILE_PREVIEW_LIST}>
        <div className="flex overflow-x-auto gap-2 scrollbar-thin scrollbar-thumb-zinc-300 dark:scrollbar-thumb-zinc-600 scrollbar-track-transparent">
          {files.map((file) => {
            // Show skeleton UI when file is loading (but not for images with localUrl)
            const isImage = file.isImage || file.type?.startsWith("image/");
            const imageUrl =
              file.publicUrl ||
              file.visionMetadata?.publicUrl ||
              file.url ||
              file.localUrl;

            if (
              (file.isLoading ||
                file.status === "loading" ||
                file.status === "downloading" ||
                file.status === "uploading") &&
              !(isImage && imageUrl) // Don't show skeleton for images that have a URL to display
            ) {
              // Determine status text and icon
              const getStatusInfo = () => {
                switch (file.status) {
                  case "downloading":
                    return {
                      text: "Downloading...",
                      icon: Download,
                      color: "text-blue-500",
                    };
                  case "uploading":
                    return {
                      text: "Uploading...",
                      icon: Upload,
                      color: "text-purple-500",
                    };
                  case "loading":
                  default:
                    return {
                      text: "Loading...",
                      icon: Loader2,
                      color: "text-zinc-500",
                    };
                }
              };

              const statusInfo = getStatusInfo();
              const StatusIcon = statusInfo.icon;

              // Image skeleton with status - shorter with rounded corners
              if (file.isImage) {
                return (
                  <div
                    key={file.id || file.name}
                    className="relative flex-shrink-0 rounded-2xl overflow-hidden"
                  >
                    <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-800 flex flex-col items-center justify-center">
                      <StatusIcon
                        className={`h-4 w-4 ${statusInfo.color} ${statusInfo.icon === Loader2 ? "animate-spin" : ""}`}
                      />
                      <div className="text-[8px] text-zinc-500 dark:text-zinc-400 mt-1 text-center">
                        {statusInfo.text}
                      </div>
                    </div>
                    {/* Google Drive badge for Google Drive files */}
                    {file.isGoogleDrive && (
                      <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 flex items-center justify-center shadow-sm">
                        <GoogleDriveIcon className="h-2 w-2" />
                      </div>
                    )}
                  </div>
                );
              }

              // Document/file skeleton with status - macOS style
              const fileBackgroundColor = getFileBackgroundColor(file.name);
              const FileIconComponent = getFileIcon(file.name);
              const isPdf = file.name?.toLowerCase().endsWith(".pdf");
              const fileTypeLabel = isPdf ? "PDF" : "File";

              return (
                <div
                  key={file.id || file.name}
                  className="group relative flex items-center gap-2 px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded-2xl border border-zinc-200/50 dark:border-zinc-700/50 min-w-[240px] flex-shrink-0 transition-colors"
                >
                  {/* File icon with colored background - even ratio square */}
                  <div
                    className={`flex-shrink-0 w-8 h-8 ${fileBackgroundColor} rounded-lg flex items-center justify-center relative`}
                  >
                    {/* Show spinner instead of file icon during loading states */}
                    <Loader2 className="h-4 w-4 text-white drop-shadow-lg animate-spin" />

                    {/* Google Drive badge */}
                    {file.isGoogleDrive && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 flex items-center justify-center shadow-sm">
                        <GoogleDriveIcon className="h-1.5 w-1.5" />
                      </div>
                    )}
                  </div>

                  {/* File info */}
                  <div className="flex-1 min-w-0 space-y-0">
                    <div className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                      {file.name}
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      {fileTypeLabel}
                    </div>
                  </div>

                  {/* Remove button - top right corner */}
                  <button
                    onClick={() => onRemoveFile(file)}
                    className="absolute top-2 right-2 w-4 h-4 rounded-full bg-black/80 hover:bg-black flex items-center justify-center transition-all duration-200"
                    title="Remove file"
                  >
                    <X className="h-3.5 w-3.5 text-white" />
                  </button>
                </div>
              );
            }

            // imageUrl is already calculated above

            if (isImage && imageUrl) {
              // Check if image is currently uploading
              const isUploading =
                globalIsUploading ||
                file.status === "uploading" ||
                file.status === "loading" ||
                file.isLoading === true;

              // Image preview with optional spinner overlay - shorter with rounded corners
              return (
                <div
                  key={file.id || file.name}
                  className="group relative flex-shrink-0 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600 transition-colors bg-zinc-100 dark:bg-zinc-800 max-h-14"
                >
                  <img
                    src={imageUrl}
                    alt={file.name}
                    className={`w-auto h-14 object-contain cursor-pointer transition-opacity duration-200 block ${
                      isUploading ? "opacity-75" : "opacity-100"
                    }`}
                    onClick={() =>
                      onImageClick({ url: imageUrl, name: file.name })
                    }
                    onError={(e) => {
                      console.error("Image failed to load:", imageUrl);
                      e.target.style.display = "none";
                    }}
                  />

                  {/* Spinner overlay for uploading images */}
                  {isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 text-white drop-shadow-lg animate-spin" />
                    </div>
                  )}

                  {/* Google Drive badge for Google Drive files */}
                  {file.isGoogleDrive && (
                    <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 flex items-center justify-center shadow-sm">
                      <GoogleDriveIcon className="h-2 w-2" />
                    </div>
                  )}

                  <button
                    onClick={() => onRemoveFile(file)}
                    className="absolute top-1 right-1 w-4 h-4 rounded-full bg-black/80 hover:bg-black flex items-center justify-center transition-all duration-200"
                    title="Remove image"
                  >
                    <X className="h-3.5 w-3.5 text-white" />
                  </button>
                </div>
              );
            }

            // Non-image files - macOS style

            const fileBackgroundColor = getFileBackgroundColor(file.name);
            const FileIconComponent = getFileIcon(file.name);
            const isPdf = file.name?.toLowerCase().endsWith(".pdf");
            const isTextFile = file.name
              ?.toLowerCase()
              .match(
                /\.(txt|md|json|js|jsx|ts|tsx|html|css|py|java|c|cpp|php|xml|yaml|yml|csv)$/
              );
            const fileTypeLabel = isPdf ? "PDF" : "File";
            const isClickable = isPdf || isTextFile;
            // Check if file is currently uploading
            const isUploading =
              globalIsUploading ||
              file.status === "uploading" ||
              file.status === "loading" ||
              file.isLoading === true;

            return (
              <div
                key={file.id || file.name}
                className={`group relative flex items-center gap-2 px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded-2xl border border-zinc-200/50 dark:border-zinc-700/50 min-w-[240px] flex-shrink-0 transition-colors ${
                  isClickable
                    ? "cursor-pointer hover:bg-zinc-200 dark:hover:bg-zinc-700"
                    : ""
                }`}
                onClick={() => isClickable && handleFileClick(file)}
              >
                {/* File icon with colored background - even ratio square */}
                <div
                  className={`flex-shrink-0 w-8 h-8 ${fileBackgroundColor} rounded-lg flex items-center justify-center relative overflow-visible`}
                >
                  {/* Show spinner when uploading, otherwise show file icon */}
                  {isUploading ? (
                    <div className="relative">
                      <Loader2
                        className="h-4 w-4 text-white drop-shadow-lg animate-spin z-[9999] relative"
                        style={{ zIndex: 9999 }}
                      />
                      {/* Debug indicator */}
                      <div className="absolute -top-2 -right-2 w-2 h-2 bg-red-500 rounded-full"></div>
                    </div>
                  ) : (
                    <FileIconComponent className="h-4 w-4 text-white" />
                  )}

                  {/* Google Drive badge */}
                  {file.isGoogleDrive && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 flex items-center justify-center shadow-sm">
                      <GoogleDriveIcon className="h-1.5 w-1.5" />
                    </div>
                  )}
                </div>

                {/* File info */}
                <div className="flex-1 min-w-0 space-y-0">
                  <div className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                    {file.name}
                  </div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">
                    {fileTypeLabel}
                  </div>
                </div>

                {/* Remove button - top right corner */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFile(file);
                  }}
                  className="absolute top-2 right-2 w-4 h-4 rounded-full bg-black/80 hover:bg-black flex items-center justify-center transition-all duration-200"
                  title="Remove file"
                >
                  <X className="h-3.5 w-3.5 text-white" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <FileViewerModal
        isOpen={isViewerOpen}
        onClose={closeViewer}
        file={viewerFile}
      />
    </>
  );
}
